import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiClient } from '../../api';
import Drawer from '../UI/Drawer';
import { primaryBtn, primaryBtnStyle, ghostBtn, ghostBtnStyle, inputCls, inputStyle, fieldLabel, fieldLabelStyle, errMessage } from '../UI/ink';
import { bandFg } from './model';

const CRIT = [['low', 'LOW', 2], ['med', 'MEDIUM', 5], ['high', 'HIGH', 7], ['crit', 'CRITICAL', 9]];
const TYPES = [['server', 'Server'], ['network', 'Network device'], ['workstation', 'Endpoint'], ['iot', 'IoT device']];
const EMPTY = { name: '', ip: '', type: 'server', crit: 'med', location: '' };

const AddAssetDrawer = ({ open, onClose, onAdded, initialIp = '' }) => {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => { if (open) { setForm({ ...EMPTY, ip: initialIp }); setError(null); } }, [open, initialIp]);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e?.preventDefault();
    if (!form.name.trim() || !form.ip.trim()) { setError('Name and IP address are required.'); return; }
    setBusy(true);
    setError(null);
    try {
      await apiClient.post('/api/register', {
        hostname: form.name.trim(),
        ip_address: form.ip.trim(),
        os_type: form.type,
        os_info: '',
        device_type: form.type,
        criticality_score: CRIT.find(([k]) => k === form.crit)[2],
        location: form.location.trim() || null,
      });
      await onAdded();
      onClose();
    } catch (err) {
      setError(errMessage(err, 'Failed to add asset.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      kicker="ASSETS"
      title="Add asset manually"
      subtitle={(
        <>For devices that can’t run the agent. To monitor a server fully, install the agent from <Link to="/agents" style={{ color: 'var(--ink-link)' }}>Agent Registry</Link> instead.</>
      )}
      footer={(
        <>
          <span />
          <div className="flex gap-2.5">
            <button type="button" onClick={onClose} className={ghostBtn} style={ghostBtnStyle}>Cancel</button>
            <button type="submit" form="add-asset-form" disabled={busy} className={primaryBtn} style={primaryBtnStyle}>{busy ? 'Adding…' : 'Add asset'}</button>
          </div>
        </>
      )}
    >
      <form id="add-asset-form" onSubmit={submit} className="flex flex-col gap-5">
        {error && (
          <div role="alert" className="px-3.5 py-2.5 rounded-[10px] border text-[13px]" style={{ borderColor: 'var(--sev-crit-fg)', color: 'var(--sev-crit-fg)', background: 'var(--sev-crit-bg)' }}>{error}</div>
        )}
        <div className="flex flex-col gap-1.5">
          <label htmlFor="add-name" className={fieldLabel} style={fieldLabelStyle}>Asset name</label>
          <input id="add-name" type="text" required value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. core-switch-02" className={inputCls} style={inputStyle} />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
          <div className="flex flex-col gap-1.5">
            <label htmlFor="add-ip" className={fieldLabel} style={fieldLabelStyle}>IP address</label>
            <input id="add-ip" type="text" required value={form.ip} onChange={e => set('ip', e.target.value)} placeholder="10.0.0.0" className={`${inputCls} font-mono`} style={inputStyle} />
          </div>
          <div className="flex flex-col gap-1.5">
            <label htmlFor="add-type" className={fieldLabel} style={fieldLabelStyle}>Device type</label>
            <select id="add-type" value={form.type} onChange={e => set('type', e.target.value)} className={inputCls} style={inputStyle}>
              {TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
        </div>
        <fieldset className="m-0 p-0 border-0 flex flex-col gap-2">
          <legend className={`p-0 mb-2 ${fieldLabel}`} style={fieldLabelStyle}>Criticality</legend>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {CRIT.map(([k, label]) => {
              const on = form.crit === k;
              return (
                <button key={k} type="button" onClick={() => set('crit', k)} aria-pressed={on} className="h-11 flex items-center justify-center gap-[7px] rounded-[10px] border font-mono text-[11.5px] tracking-[0.04em]" style={{ borderColor: on ? 'var(--accent)' : 'var(--surf-line)', background: on ? 'var(--surf-raised)' : 'var(--surf-bg)' }}>
                  <span className="w-[7px] h-[7px] rounded-[1px]" style={{ background: bandFg(k) }} />{label}
                </button>
              );
            })}
          </div>
          <span className="text-xs" style={{ color: 'var(--ink-faint)' }}>How much damage losing this asset would do. This is separate from its measured risk score.</span>
        </fieldset>
        <div className="flex flex-col gap-1.5">
          <label htmlFor="add-loc" className={fieldLabel} style={fieldLabelStyle}>Location <span className="font-normal" style={{ color: 'var(--ink-faint)' }}>(used for SNMP)</span></label>
          <input id="add-loc" type="text" value={form.location} onChange={e => set('location', e.target.value)} placeholder="e.g. Block-A" className={inputCls} style={inputStyle} />
        </div>
      </form>
    </Drawer>
  );
};

export default AddAssetDrawer;
